import React, { useCallback, useEffect, useMemo, useState } from "react";
import swal from "sweetalert";
import axiosInstance from "../../../redux/api";
import Spinner from "../../Layouts/Spinner";
import "./ZatcaDowntimeHold.css";

const ZatcaDowntimeHold = () => {
  const [config, setConfig] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    active: false,
    starts_at_riyadh: "",
    ends_at_riyadh: "",
  });
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);

  const actionableIds = useMemo(
    () =>
      items
        .filter((item) => !["reported", "cleared"].includes(item.status))
        .map((item) => item.id),
    [items]
  );

  const allSelected =
    actionableIds.length > 0 &&
    actionableIds.every((id) => selected.includes(id));

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [configResponse, queueResponse] = await Promise.all([
        axiosInstance.get("zatca_hold/config"),
        axiosInstance.post("zatca_hold/queue/list", { limit: 250 }),
      ]);

      const configData = configResponse?.data?.response;
      const queueData = queueResponse?.data?.response;
      window.dispatchEvent(
        new CustomEvent("zatcaHoldConfigUpdated", { detail: configData || {} })
      );
      setConfig(configData);
      setItems(queueData?.items || []);
      setForm({
        active: Boolean(configData?.active),
        starts_at_riyadh: configData?.starts_at_input || "",
        ends_at_riyadh: configData?.ends_at_input || "",
      });
      setSelected([]);
    } catch (error) {
      console.error(error);
      swal({ text: "Unable to load ZATCA hold data", icon: "error", button: "Close" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveConfig = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await axiosInstance.post("zatca_hold/config", {
        active: form.active,
        starts_at_riyadh: form.starts_at_riyadh,
        ends_at_riyadh: form.ends_at_riyadh,
      });
      if (response?.data?.status) {
        swal({ text: "ZATCA hold settings saved", icon: "success", button: "Close", timer: 1500 });
        await loadData();
      } else {
        swal({ text: response?.data?.message || "Unable to save", icon: "error", button: "Close" });
      }
    } catch (error) {
      console.error(error);
      swal({ text: "Unable to save ZATCA hold settings", icon: "error", button: "Close" });
    } finally {
      setSaving(false);
    }
  };

  const toggleSelected = (id) => {
    setSelected((current) =>
      current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id]
    );
  };

  const toggleSelectAll = () => {
    setSelected(allSelected ? [] : actionableIds);
  };

  const runAction = async (path, successText) => {
    if (!selected.length) {
      swal({ text: "Select at least one row", icon: "warning", button: "Close" });
      return;
    }
    setActing(true);
    try {
      const response = await axiosInstance.post(path, { ids: selected });
      if (response?.data?.status) {
        swal({ text: successText, icon: "success", button: "Close", timer: 1500 });
        await loadData();
      } else {
        swal({ text: response?.data?.message || "Action failed", icon: "error", button: "Close" });
      }
    } catch (error) {
      console.error(error);
      swal({ text: "Action failed", icon: "error", button: "Close" });
    } finally {
      setActing(false);
    }
  };

  const formatMoney = (value) =>
    new Intl.NumberFormat("en-SA", {
      style: "currency",
      currency: "SAR",
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  return (
    <div className="zatca-hold-page">
      <form className="category-wrapper" onSubmit={saveConfig}>
        <h3 className="pb-3">ZATCA Downtime Hold</h3>
        <div className="zatca-hold-toolbar">
          <div className="zatca-hold-field">
            <label>Riyadh start</label>
            <input
              type="datetime-local"
              value={form.starts_at_riyadh}
              onChange={(event) =>
                setForm((current) => ({ ...current, starts_at_riyadh: event.target.value }))
              }
            />
          </div>
          <div className="zatca-hold-field">
            <label>Riyadh end</label>
            <input
              type="datetime-local"
              value={form.ends_at_riyadh}
              onChange={(event) =>
                setForm((current) => ({ ...current, ends_at_riyadh: event.target.value }))
              }
            />
          </div>
          <div className="zatca-hold-toggle">
            <label>Active hold</label>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(event) =>
                setForm((current) => ({ ...current, active: event.target.checked }))
              }
            />
            <span>{form.active ? "Active" : "Inactive"}</span>
          </div>
          <div className="zatca-hold-actions">
            <button className="zatca-hold-btn" type="submit" disabled={saving || loading}>
              {saving ? <Spinner /> : "Save"}
            </button>
            <button
              className="zatca-hold-btn secondary"
              type="button"
              onClick={loadData}
              disabled={loading}
            >
              {loading ? <Spinner /> : "Refresh"}
            </button>
          </div>
        </div>
        <div className="zatca-hold-summary">
          <span className={`zatca-hold-chip ${config?.is_holding_now ? "active" : ""}`}>
            {config?.is_holding_now ? "Holding now" : "Not holding"}
          </span>
          <span className="zatca-hold-chip">Batch {config?.flush_batch_size || 0}</span>
          <span className="zatca-hold-chip">Gap {config?.flush_gap_seconds || 0}s</span>
          <span className="zatca-hold-chip">Retries {config?.max_attempts || 0}</span>
        </div>
      </form>

      <div className="category-wrapper mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: "10px" }}>
          <h3 className="m-0">Held ZATCA Queue</h3>
          <div className="zatca-hold-actions">
            <button
              className="zatca-hold-btn"
              type="button"
              disabled={acting || selected.length === 0}
              onClick={() => runAction("zatca_hold/queue/submit_now", "Selected rows submitted")}
            >
              Submit now
            </button>
            <button
              className="zatca-hold-btn danger"
              type="button"
              disabled={acting || selected.length === 0}
              onClick={() => runAction("zatca_hold/queue/remove", "Selected rows marked manually handled")}
            >
              Remove from queue
            </button>
          </div>
        </div>

        <div className="zatca-hold-table-wrap">
          <table className="zatca-hold-table">
            <thead>
              <tr>
                <th style={{ width: "42px" }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    disabled={actionableIds.length === 0}
                  />
                </th>
                <th>Invoice No.</th>
                <th>Environment</th>
                <th>Type</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Created</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Next attempt</th>
                <th>Last error</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan="11" className="text-center">
                    No queued invoices
                  </td>
                </tr>
              )}
              {items.map((item) => {
                const disabled = ["reported", "cleared"].includes(item.status);
                return (
                  <tr key={item.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.includes(item.id)}
                        onChange={() => toggleSelected(item.id)}
                        disabled={disabled}
                      />
                    </td>
                    <td>{item.invoice_number || "-"}</td>
                    <td>
                      <span className={`sf-env-badge ${item.is_test ? "sandbox" : "live"}`}>
                        {item.is_test ? "TEST" : "LIVE"}
                      </span>
                    </td>
                    <td>{item.invoice_type_label || item.submission_type || "-"}</td>
                    <td>{item.customer_name || (item.customer_user_id ? `Customer ${item.customer_user_id}` : "-")}</td>
                    <td>{formatMoney(item.amount)}</td>
                    <td>{item.invoice_created_on || "-"}</td>
                    <td>
                      <span className={`zatca-hold-status ${item.status}`}>
                        {item.status_label || item.status}
                      </span>
                    </td>
                    <td>
                      {item.attempts || 0}/{item.max_attempts || 0}
                    </td>
                    <td>{item.next_attempt_on || "-"}</td>
                    <td className="zatca-hold-error" title={item.last_error || ""}>
                      {item.last_error || "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ZatcaDowntimeHold;
